import React, { useContext, useEffect, useMemo, useState } from "react";
import { Input, Tooltip, Upload } from "antd";
import {
  EditOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import "./styles.css";
import { taskFieldAttachmentRoutes } from "~/http/routes";
import { ToastContext } from "~/providers/ToastProvider";

function UploadMidias(props) {
  const {
    label,
    fileList,
    maxCount,
    onPreview,
    beforeUpload,
    enableEditingDescription,
    taskId,
    subprojectId,
    ...rest
  } = props;

  const [internalFileList, setInternalFileList] = useState(fileList || []);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setToast } = useContext(ToastContext);

  const cloudFrontUrl =
    process.env.REACT_APP_CLOUD_FRONT_URL ||
    "https://d1ct5h0jsyk8ln.cloudfront.net/api_app";

  useEffect(() => {
    setInternalFileList(fileList || []);
  }, [fileList]);

  const updatedFileList = useMemo(() => {
    return internalFileList.map((file) => {
      const url = file.url?.startsWith(cloudFrontUrl)
        ? file.url
        : `${cloudFrontUrl}/${file.url.split("/file/")[1]}`;
      return { ...file, url };
    });
  }, [internalFileList, cloudFrontUrl]);

  async function handlePreview(file) {
    if (onPreview) return onPreview(file);

    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }

    const image = new Image();
    image.src = src;

    const imgWindow = window.open(src);
    imgWindow.document.write(image.outerHTML);
  }

  const handleEditClick = (file) => {
    setEditingFileId(file.uid);
    setEditingValue(file.description || "");
  };

  const handleInputChange = (e) => {
    setEditingValue(e.target.value);
  };

  const handleInputKeyDown = (e, file) => {
    if (e.key === "Enter") {
      handleEditDescription(file);
    }
  };

  async function handleEditDescription(file) {
    // if (!editingValue.trim()) {
    //   setEditingFileId(null);
    //   return;
    // }

    setIsSubmitting(true);

    try {
      const body = { description: editingValue };
      const res = await taskFieldAttachmentRoutes.update(
        subprojectId,
        taskId,
        file.task_field_id,
        file.id,
        body,
      );

      const attachment = res.data.data;

      setInternalFileList((prev) =>
        prev.map((f) =>
          f.id === attachment.id
            ? { ...f, description: attachment.description }
            : f,
        ),
      );

      setToast({
        type: "success",
        title: "Sucesso!",
        message: "Mídia atualizada com sucesso!",
        show: true,
        autohide: true,
      });

      setEditingFileId(null);
    } catch (err) {
      console.error(err);
      setToast({
        type: "error",
        title: "Erro!",
        message: "Ocorreu um erro ao atualizar a descrição!",
        show: true,
        autohide: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <p>
        {label}
        {maxCount && (
          <small className="ml-2">{`(Máximo ${maxCount} arquivos)`}</small>
        )}
      </p>

      <Upload
        {...rest}
        accept={rest.accept ?? "image/png, image/gif, image/jpeg"}
        listType="picture-card"
        fileList={updatedFileList}
        onPreview={handlePreview}
        maxCount={maxCount}
        beforeUpload={beforeUpload}
        itemRender={(originNode, file) => (
          <div
            className="custom-upload-card"
            style={{
              width: 150,
              height: 150,
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              transition: "all 0.2s ease-in-out",
            }}
          >
            <div
              className="custom-upload-image"
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fafafa",
                borderBottom: "1px solid #f0f0f0",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {originNode}
              </div>
            </div>

            <div
              style={{
                padding: "6px 8px",
                fontSize: 12,
                textAlign: "left",
                position: "relative",
              }}
            >
              {editingFileId === file.uid ? (
                <Input
                  value={editingValue}
                  onChange={handleInputChange}
                  onKeyDown={(e) => handleInputKeyDown(e, file)}
                  disabled={isSubmitting}
                  autoFocus
                  size="small"
                />
              ) : (
                <>
                  {enableEditingDescription && (
                    <Tooltip title="Editar descrição">
                      <EditOutlined
                        onClick={() => handleEditClick(file)}
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          fontSize: 16,
                          color: "#1890FF",
                          cursor: "pointer",
                          background: "white",
                          borderRadius: 4,
                          padding: 4,
                          transition: "all 0.2s ease-in-out",
                        }}
                      />
                    </Tooltip>
                  )}
                  <span
                    style={{
                      display: "block",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={file.description}
                  >
                    {file.description || "Sem descrição"}
                  </span>
                  {file.created_at && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "#888",
                        display: "block",
                        marginTop: 2,
                      }}
                    >
                      {new Date(file.created_at).toLocaleDateString("pt-BR")}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      >
        <div className="upload-file">
          {maxCount && internalFileList.length === maxCount ? (
            <>
              <ExclamationCircleOutlined className="upload-icon" />
              <p>{`Limite de upload atingido! (Máximo ${maxCount})`}</p>
            </>
          ) : (
            <>
              <UploadOutlined className="upload-icon" />
              <p>Clique ou arraste e solte para adicionar</p>
            </>
          )}
        </div>
      </Upload>
    </>
  );
}

export default UploadMidias;
